import CommonCardLayout from "@/features/dashboard/components/CommonCardLayout";
import {
  Building2,
  CircleCheckBig,
  FileClock,
  RefreshCw,
  Users,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useReviewTrialRequest, useFetchUsersList } from "@/shared/hooks";
import CustomTable from "@/shared/components/ui/Button/CustomTable";
import {
  ACCESS_REQUEST_COLUMNS,
  createAccessRequestRenderCell,
} from "@/shared/utils/commonConfigs";

const SuperAdminDashboard = () => {
  const {
    data: allUserList,
    refetch,
    isFetching,
  } = useFetchUsersList({ limit: 100, offset: 0 });
  const { mutate: reviewTrialRequest, isPending: isPendingReviewTrialRequest } =
    useReviewTrialRequest();

  const handleReviewTrialRequest = useCallback(
    (userId, action) => reviewTrialRequest({ userId, action }),
    [reviewTrialRequest],
  );

  const accessRequestRenderCell = useMemo(
    () =>
      createAccessRequestRenderCell(
        handleReviewTrialRequest,
        isPendingReviewTrialRequest,
      ),
    [handleReviewTrialRequest, isPendingReviewTrialRequest],
  );

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const { pendingUsers, stats } = useMemo(() => {
    const list = allUserList ?? [];
    const totalUsers = list.length;
    const pending = list.filter(
      (u) =>
        u.trial_approved_at == null &&
        u.plan !== "reject" &&
        u.role === "org_admin",
    );
    const pendingCount = pending.length;
    const activePlans = list.filter(
      (u) => u.trial_approved_at !== null,
    ).length;
    const totalOrganizations = list.filter(
      (u) => u.role === "org_admin",
    ).length;

    return {
      pendingUsers: pending,
      stats: [
        {
          title: "Total Organizations",
          value: totalOrganizations,
          icon: (
            <Building2
              className="text-secondary"
              size={22}
              strokeWidth={1.5}
            />
          ),
        },
        {
          title: "Pending Organization Requests",
          value: pendingCount,
          icon: (
            <FileClock
              className="text-warning-500"
              size={22}
              strokeWidth={1.5}
            />
          ),
        },
        {
          title: "Active Plans",
          value: activePlans,
          icon: (
            <Users
              className="text-success-500"
              size={22}
              strokeWidth={1.5}
            />
          ),
        },
        {
          title: "Total Users",
          value: totalUsers,
          icon: (
            <CircleCheckBig
              className="text-success-500"
              size={22}
              strokeWidth={1.5}
            />
          ),
        },
      ],
    };
  }, [allUserList]);

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold text-gray-800">
        Super Admin Dashboard
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm font-medium">
                {stat.title}
              </span>
              {stat.icon}
            </div>
            <p className="text-2xl font-bold text-gray-800">
              {Number.isFinite(stat.value) ? stat.value : 0}
            </p>
          </div>
        ))}
      </div>

      <CommonCardLayout
        title="Pending Organization Requests"
        headerRight={
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 text-sm text-secondary hover:text-secondary/80 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              size={14}
              className={isFetching ? "animate-spin" : ""}
            />
            Refresh
          </button>
        }
      >
        {pendingUsers?.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">
            No pending organization requests.
          </p>
        ) : (
          <CustomTable
            data={pendingUsers}
            columns={ACCESS_REQUEST_COLUMNS}
            height={"min(500px, calc(100dvh - 295px))"}
            renderCell={accessRequestRenderCell}
            totalCount={pendingUsers?.length ?? 0}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}
      </CommonCardLayout>
    </div>
  );
};
export default SuperAdminDashboard;
